import { SupabaseClient } from '@supabase/supabase-js';

import { Database, withLogs } from '@core';

import { apiClient, ApiConfig } from '../apiClient';
import { Club } from '../clubs/clubs';
import { Tag } from '../tags/tags';

export type ActivityListItem = {
    id: number;
    userId: string;
    startTime: string;
    endTime: string | null;
    boothSessionId: number;
    clubId: number | null;
    activitySwingsSequence: number | null;
    club?: Club | null;
    tags?: Tag[];
    swingCount?: number;
    favoriteSwingsCount?: number;
    lastLiveMeasurementAt: string;
};

export type ActivityResponse = {
    id: number;
    userId: string;
    startTime: string;
    endTime: string | null;
    boothSessionId: number;
    clubId: number | null;
    activitySwingsSequence: number | null;
    club?: Club | null;
    tags?: Tag[];
    lastLiveMeasurementAt: string;
    swings: { uuid: string; favorite: boolean }[];
};

// TODO: rework this along activity context.
// TODO: switch to camelcase
export type FetchAllUserActivitiesParams = {
    userId?: string;
    startTime: string | null;
    endTime: string | null;
    clubId: number | null;
    clubTypeId: number | null;
    hasClub: boolean | null;
    hasFavorite: boolean | null;
    favoriteText: string | null;
    boothId: number | null;
    offset: number;
    limit: number;
    tags: number[] | null;
};

export const fetchAllUserActivities = withLogs(
    async ({
        userId,
        startTime,
        endTime,
        clubId,
        hasFavorite,
        clubTypeId,
        hasClub,
        favoriteText,
        boothId,
        tags,
        offset = 0,
        limit = 200,
    }: FetchAllUserActivitiesParams): Promise<ActivityListItem[]> => {
        if (!userId) {
            throw Error('no user id provided');
        }

        const { data } = await apiClient.get<ActivityResponse[]>(ApiConfig.path(`api/v1/users/${userId}/activities`), {
            params: {
                start_time: startTime,
                end_time: endTime,
                club_id: clubId,
                club_type_id: clubTypeId,
                has_club: hasClub,
                has_favorite_swing: hasFavorite,
                favorite_text: favoriteText,
                booth_id: boothId,
                tags,
                offset,
                limit,
            },
        });
        return data.map((activity) => ({
            ...activity,
            swingCount: activity.swings.length,
            favoriteSwingsCount: activity.swings.filter((swing) => Boolean(swing.favorite)).length,
        }));
    },
    'fetchAllUserActivities',
);

export const fetchActivityCount = withLogs(
    async (supabase: SupabaseClient<Database>, userId: string): Promise<number | null> => {
        const { count, error } = await supabase
            .from('activities')
            .select('id, swings!inner(id)', {
                count: 'exact',
                head: true,
            })
            .eq('deleted', false)
            .eq('user_id', userId);

        if (error) {
            throw error;
        }
        return count;
    },
    'fetchActivityCount',
);
